import { DeploymentCheckThrottler } from "./DeploymentCheckThrottler";
import { DeploymentPollScheduler } from "./DeploymentPollScheduler";
import {
  isDeploymentChanged,
  probeDeployment,
  reloadApplication,
} from "./deploymentService";
import type { ProbeOutcome } from "./deploymentService";
import type {
  DeploymentConfig,
  DeploymentFingerprint,
  DeploymentPhase,
} from "./types";

export interface DeploymentMonitorDelegate {
  onPhaseChange(phase: DeploymentPhase): void;
  onCountdownTick(seconds: number): void;
}

export interface DeploymentMonitorOptions {
  config: DeploymentConfig;
  delegate: DeploymentMonitorDelegate;
  probe?: (signal?: AbortSignal) => Promise<ProbeOutcome>;
}

/**
 * Framework-agnostic orchestrator for deployment detection lifecycle.
 * React hooks delegate to this class; infrastructure tests target it directly.
 */
export class DeploymentMonitor {
  private readonly config: DeploymentConfig;
  private readonly delegate: DeploymentMonitorDelegate;
  private readonly probe: (signal?: AbortSignal) => Promise<ProbeOutcome>;
  private readonly throttler: DeploymentCheckThrottler;

  private baseline: DeploymentFingerprint | null = null;
  private phase: DeploymentPhase = "monitoring";
  private checkInFlight = false;
  private abortController: AbortController | null = null;
  private scheduler: DeploymentPollScheduler | null = null;
  private routeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setTimeout> | null = null;
  private reloadGuard = false;
  private updatePendingGuard = false;
  private countdownSeconds: number;
  private routeInitialized = false;
  private lastPathname: string | null = null;

  constructor(options: DeploymentMonitorOptions) {
    this.config = options.config;
    this.delegate = options.delegate;
    this.probe =
      options.probe ??
      ((signal) => probeDeployment(signal, this.config.checkUrl));
    this.throttler = new DeploymentCheckThrottler(
      this.config.minimumCheckGapMs,
    );
    this.countdownSeconds = this.config.countdownSeconds;
  }

  getPhase(): DeploymentPhase {
    return this.phase;
  }

  getCountdownSeconds(): number {
    return this.countdownSeconds;
  }

  getBaseline(): DeploymentFingerprint | null {
    return this.baseline;
  }

  isPolling(): boolean {
    return this.scheduler?.isRunning() ?? false;
  }

  async startup(): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    await this.runCheck();
  }

  async runCheck(): Promise<void> {
    if (!this.config.enabled || this.phase !== "monitoring") {
      return;
    }

    if (this.checkInFlight) {
      return;
    }

    if (!this.throttler.canCheck()) {
      return;
    }

    this.checkInFlight = true;
    this.throttler.markChecked();

    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;

    try {
      const result = await this.probe(controller.signal);

      if (result.outcome !== "success") {
        return;
      }

      const { fingerprint } = result;

      if (this.baseline === null) {
        this.baseline = fingerprint;
        return;
      }

      if (isDeploymentChanged(this.baseline, fingerprint)) {
        this.enterUpdatePending(fingerprint);
      }
    } finally {
      this.checkInFlight = false;
    }
  }

  onRouteChange(pathname: string): void {
    if (!this.config.enabled || !this.config.enableRouteChangeCheck) {
      return;
    }

    if (!this.routeInitialized) {
      this.routeInitialized = true;
      this.lastPathname = pathname;
      return;
    }

    if (this.lastPathname === pathname) {
      return;
    }

    this.lastPathname = pathname;

    if (this.routeDebounceTimer !== null) {
      clearTimeout(this.routeDebounceTimer);
    }

    this.routeDebounceTimer = setTimeout(() => {
      this.routeDebounceTimer = null;
      void this.runCheck();
    }, this.config.routeCheckDebounceMs);
  }

  onVisibilityChange(isVisible: boolean): void {
    if (!this.config.enabled) {
      return;
    }

    if (isVisible) {
      if (this.config.enableVisibilityCheck) {
        void this.runCheck();
      }
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  /** Used by cross-tab sync adapters to force the update flow. */
  forceUpdateDetected(fingerprint?: DeploymentFingerprint): void {
    this.enterUpdatePending(
      fingerprint ?? this.baseline ?? { etag: null, lastModified: null },
    );
  }

  requestReload(): void {
    if (this.reloadGuard) {
      return;
    }

    this.reloadGuard = true;
    reloadApplication();
  }

  destroy(): void {
    this.abortController?.abort();
    this.stopPolling();
    this.clearRouteDebounce();
    this.clearCountdown();
  }

  private enterUpdatePending(fingerprint: DeploymentFingerprint): void {
    if (this.phase !== "monitoring" || this.updatePendingGuard) {
      return;
    }

    this.updatePendingGuard = true;
    this.phase = "update-pending";
    this.delegate.onPhaseChange("update-pending");

    this.countdownSeconds = this.config.countdownSeconds;
    this.delegate.onCountdownTick(this.countdownSeconds);

    this.stopPolling();
    this.abortController?.abort();
    this.clearRouteDebounce();
    this.startCountdown();

    this.config.syncAdapter?.notifyUpdateDetected?.(fingerprint);
  }

  private startCountdown(): void {
    this.clearCountdown();

    if (this.countdownSeconds <= 0) {
      this.requestReload();
      return;
    }

    this.countdownTimer = setTimeout(() => {
      this.countdownTimer = null;
      this.countdownSeconds -= 1;
      this.delegate.onCountdownTick(this.countdownSeconds);

      if (this.countdownSeconds <= 0) {
        this.requestReload();
      } else {
        this.startCountdown();
      }
    }, 1000);
  }

  private startPolling(): void {
    if (!this.config.enabled || this.phase !== "monitoring") {
      return;
    }

    if (this.scheduler === null) {
      this.scheduler = new DeploymentPollScheduler(
        () => {
          void this.runCheck();
        },
        this.config.pollIntervalMs,
        this.config.randomPollJitterMs,
      );
    }

    this.scheduler.start();
  }

  private stopPolling(): void {
    this.scheduler?.stop();
  }

  private clearRouteDebounce(): void {
    if (this.routeDebounceTimer !== null) {
      clearTimeout(this.routeDebounceTimer);
      this.routeDebounceTimer = null;
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
